import React, { useState } from 'react';
import './ProteinIntakeCalculator.css';

const KG_PER_LB = 0.45359237;
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const round = (n, d=0) => Math.round(n * 10**d) / 10**d;
const toKg = (w, u) => (u === 'kg' ? w : w * KG_PER_LB);

const goalRanges = {
  maintenance: [0.8, 1.0],
  muscle: [1.0, 1.2],
  fatloss: [1.2, 1.4],
};

function ProteinIntakeCalculator() {
  const [weight, setWeight] = useState(150);
  const [unit, setUnit] = useState('lbs');
  const [goal, setGoal] = useState('muscle');
  const [activity, setActivity] = useState('moderate');
  const [meals, setMeals] = useState(4);
  const [showInfo, setShowInfo] = useState(false);

  // New state for guardrails/features
  const [gentleMode, setGentleMode] = useState(false);
  const [hasCondition, setHasCondition] = useState(false);
  const [age, setAge] = useState(30);
  const [showSources, setShowSources] = useState(false);
  const [shareStatus, setShareStatus] = useState('');

  // Clamp weight and meals to valid values
  const safeWeight = clamp(+weight || 0, 1, 1000);
  const safeMeals = clamp(+meals || 1, 1, 12);
  const safeAge = clamp(+age || 0, 10, 120);

  // Convert to kg
  const weightKg = toKg(safeWeight, unit);

  // Get factors
  const [minFactor, maxFactor] = goalRanges[goal];
  const activityAdj = activity === 'very' ? 0.1 : 0;
  let minKg = clamp(minFactor + activityAdj, 0.6, 2.2);
  let maxKg = clamp(maxFactor + activityAdj, 0.6, 2.2);

  // Soft warning if maxKg > 2.0
  const showSoftWarning = maxKg > 2.0;

  // Calculate grams/day
  const minGrams = round(weightKg * minKg);
  const maxGrams = round(weightKg * maxKg);

  // g/kg and g/lb for display
  const minPerLb = round(minKg * KG_PER_LB, 2);
  const maxPerLb = round(maxKg * KG_PER_LB, 2);

  // Per meal
  const minPerMeal = round(minGrams / safeMeals);
  const maxPerMeal = round(maxGrams / safeMeals);

  // Guardrail logic
  let resultPanel;
  if (safeAge < 18) {
    resultPanel = (
      <div className="protein-note-card">
        Protein intake guidance for minors should be provided by a pediatric clinician or registered dietitian. This tool is intended for informational use by adults (18+).
      </div>
    );
  } else if (hasCondition) {
    resultPanel = (
      <div className="protein-note-card">
        <strong>Notice:</strong> If you have kidney or liver disease, are pregnant or breastfeeding, are under 18, or have (or are recovering from) an eating disorder, please consult a clinician or registered dietitian for any nutrition decisions. This tool is not suitable for setting targets in these cases.
      </div>
    );
  } else if (gentleMode) {
    resultPanel = (
      <div className="protein-results-card">
        <h2>Per Meal Estimate</h2>
        <p>
          Dividing your total across {safeMeals} meals: <strong>{minPerMeal}–{maxPerMeal} g/meal</strong>
        </p>
        <div className="protein-note-card protein-muted">
          Gentle mode reduces numeric focus. If you’re struggling with disordered eating, consider contacting local services or a healthcare provider.
        </div>
      </div>
    );
  } else {
    resultPanel = (
      <div className="protein-results-card">
        <h2>Estimated Daily Protein Range</h2>
        <p>
          <strong>{minGrams}–{maxGrams} g/day</strong>
        </p>
        <p className="protein-muted">
          ({round(minKg,2)}–{round(maxKg,2)} g/kg • {minPerLb}–{maxPerLb} g/lb)
        </p>
        <h3>Per Meal Estimate</h3>
        <p>
          Dividing your total across {safeMeals} meals: <strong>{minPerMeal}–{maxPerMeal} g/meal</strong>
        </p>
        <div className="protein-note-card protein-muted">
          For most adults, ~0.25–0.40 g/kg/meal is a typical educational range. For any personal nutrition decisions, consult a registered dietitian or healthcare provider.
        </div>
      </div>
    );
  }

  // Share button logic
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareStatus('Link copied!');
      setTimeout(() => setShareStatus(''), 2000);
    } catch {
      setShareStatus('Could not copy link.');
      setTimeout(() => setShareStatus(''), 2000);
    }
  };

  return (
    <div className="protein-tool-container fogged-card">
      <h1>
        <img
          src="https://storage.googleapis.com/heldsolutionsllc-public-bucket/ic/proteinintake_icon.png"
          alt="Protein Intake Icon"
          style={{ height: '3em', verticalAlign: 'middle', marginRight: '0.3em' }}
        />
        Protein Intake Calculator
      </h1>
      <div className="protein-input-card">
        <h2>Enter Your Info</h2>
        <div className="protein-input-group protein-inline">
          <label htmlFor="weight">Weight:</label>
          <input
            id="weight"
            type="number"
            min={1}
            value={safeWeight}
            onChange={e => setWeight(+e.target.value || 0)}
            aria-label="Body weight"
          />
          <select
            value={unit}
            onChange={e => setUnit(e.target.value)}
            aria-label="Weight unit"
          >
            <option value="lbs">lbs</option>
            <option value="kg">kg</option>
          </select>
        </div>
        <div className="protein-input-group protein-inline">
          <label htmlFor="age">Age:</label>
          <input
            id="age"
            type="number"
            min={10}
            max={120}
            value={safeAge}
            onChange={e => setAge(+e.target.value || 0)}
            aria-label="Age"
          />
        </div>
        <div className="protein-input-group">
          <label>
            Goal:
            <span
              className="protein-info-icon"
              aria-label="Show protein formula info"
              onClick={() => setShowInfo(!showInfo)}
              tabIndex={0}
              role="button"
            >ℹ️</span>
          </label>
          <select value={goal} onChange={e => setGoal(e.target.value)} aria-label="Protein goal">
            <option value="muscle">Muscle Gain</option>
            <option value="fatloss">Fat Loss</option>
            <option value="maintenance">Maintenance</option>
          </select>
          {showInfo && (
            <div className="protein-formula-info">
              <strong>Maintenance:</strong> ~0.8–1.0g/kg<br />
              <strong>Muscle Gain:</strong> ~1.0–1.2g/kg<br />
              <strong>Fat Loss:</strong> ~1.2–1.4g/kg<br />
              <strong>Very Active:</strong> Add ~0.1g/kg
            </div>
          )}
        </div>
        <div className="protein-input-group">
          <label>Activity Level:</label>
          <select value={activity} onChange={e => setActivity(e.target.value)} aria-label="Activity level">
            <option value="sedentary">Sedentary</option>
            <option value="moderate">Moderate</option>
            <option value="very">Very Active</option>
          </select>
        </div>
        <div className="protein-input-group protein-inline">
          <label htmlFor="meals">Meals per day:</label>
          <input
            id="meals"
            type="number"
            min={1}
            max={12}
            value={safeMeals}
            onChange={e => setMeals(+e.target.value || 1)}
            aria-label="Meals per day"
          />
        </div>
        <div className="protein-input-group protein-inline">
          <input
            id="hasCondition"
            type="checkbox"
            checked={hasCondition}
            onChange={e => setHasCondition(e.target.checked)}
            aria-label="I have a medical condition/pregnancy or special circumstances"
          />
          <label htmlFor="hasCondition">
            I have a medical condition/pregnancy or special circumstances
          </label>
        </div>
        <div className="protein-input-group protein-inline">
          <input
            id="gentleMode"
            type="checkbox"
            checked={gentleMode}
            onChange={e => setGentleMode(e.target.checked)}
            aria-label="Gentle mode (reduced numbers)"
          />
          <label htmlFor="gentleMode">
            Gentle mode (reduced numbers)
          </label>
        </div>
      </div>

      {/* Soft warning if maxKg > 2.0 */}
      {showSoftWarning && !gentleMode && !hasCondition && safeAge >= 18 && (
        <div className="protein-note-card" style={{ background: '#ffd16622', borderLeft: '4px solid #ffd166', color: '#b45309' }}>
          Heads up: Intakes above about <strong>2.0 g/kg/day</strong> are rarely supported by current evidence for healthy adults. For any nutrition decisions, consult a registered dietitian or healthcare provider.
        </div>
      )}

      {/* Results panel */}
      {resultPanel}

      {/* Sources panel */}
      <div className="protein-footer">
        <button
          className="protein-share-button"
          onClick={handleShare}
          aria-label="Share this tool"
        >
          🔗 Share This Tool
        </button>
        {shareStatus && <span className="protein-muted" style={{ marginLeft: '1rem' }}>{shareStatus}</span>}
        <button
          className="protein-share-button"
          style={{ marginLeft: '1rem', background: '#222', color: '#fff' }}
          onClick={() => setShowSources(!showSources)}
          aria-label="Show basis and sources"
        >
          {showSources ? 'Hide Basis & Sources' : 'Show Basis & Sources'}
        </button>
        <p className="protein-muted">Powered by ThePersonalTrainersClub.com</p>
      </div>
      {showSources && (
        <div className="protein-sources-card">
          <h3>Basis & Sources</h3>
          <ul className="protein-source-list">
            <li>RDA baseline: ~0.8 g/kg/day (sedentary adults)</li>
            <li>Athlete-style ranges: endurance ~1.2–1.4; strength ~1.4–1.8; general active ~1.2–1.7 g/kg/day</li>
            <li>Older adults (≈50+): ~1.2–1.6 g/kg/day (PROT-AGE-like guidance)</li>
            <li>Hypertrophy plateaus around ~1.6 g/kg for many</li>
          </ul>

        <ul className="protein-source-list">
  <li><a href="https://www.health.harvard.edu/blog/how-much-protein-do-you-need-every-day-201506188096">Harvard Health — How much protein do you need?</a></li>
  <li><a href="https://www.heart.org/en/healthy-living/healthy-eating/eat-smart/nutrition-basics/protein-and-heart-health">American Heart Association — Protein & Heart Health</a></li>
  <li><a href="https://nutrition.ucdavis.edu/outreach/nutr-health-info-sheets/pro-protein-requirements">UC Davis Nutrition — Protein Requirements</a></li>
  <li><a href="https://www.massgeneralbrigham.org/en/about/newsroom/articles/how-much-protein-when-working-out">Mass General Brigham — Protein when working out</a></li>
  <li><a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC7231581/">Meta-analysis — Protein intake & lean mass (PMC)</a></li>
  <li><a href="https://longevity.stanford.edu/lifestyle/2024/01/23/protein-needs-for-adults-50/">Stanford Longevity — Protein needs for adults 50+</a></li>
  <li><a href="https://academic.oup.com/biomedgerontology/article/78/Supplement_1/67/7199271">Oxford Academic — Gerontology supplement on protein</a></li>
  <li><a href="https://acl.gov/sites/default/files/nutrition/Nutrition-Needs_Protein_FINAL-2.18.20_508.pdf">ACL/PROT-AGE — Protein guidance PDF</a></li>
  <li><a href="https://pubmed.ncbi.nlm.nih.gov/26797090/">Wu (2016) — Dietary protein intake & human health (PubMed)</a></li>
  <li><a href="https://www.sciencedirect.com/science/article/pii/S2161831322003660">ScienceDirect — AMDR overview for protein</a></li>
  <li><a href="https://en.wikipedia.org/wiki/Protein_(nutrient)">Wikipedia — Protein (nutrient)</a></li>
  <li><a href="https://www.thetimes.co.uk/article/protein-mania-what-do-you-really-need-hh53psk76">The Times — “Protein mania” explainer</a></li>
  <li><a href="https://www.vox.com/future-perfect/410565/protein-muscle-gain-weightlifting-plant-based-vegan">Vox — How much protein do you really need?</a></li>
  <li><a href="https://www.gq.com/story/too-much-protein-what-happens">GQ — What happens if you eat too much protein?</a></li>
  <li><a href="https://www.eatingwell.com/plant-based-protein-benefits-11712145">EatingWell — Plant protein benefits</a></li>
  <li><a href="https://en.wikipedia.org/wiki/Protein_combining">Wikipedia — Protein combining</a></li>
  <li><a href="https://en.wikipedia.org/wiki/Healthy_diet">Wikipedia — Healthy diet</a></li>
</ul>




          <div className="protein-muted" style={{ marginTop: '0.5rem' }}>
            Last reviewed: Sept 5, 2025.
          </div>
        </div>
      )}

      {/* Disclaimer & Privacy */}
      <div className="protein-disclaimer-card">
        <strong>⚠️ Disclaimer:</strong> This tool is for informational and educational purposes only. It does not provide medical or dietetic advice. For any nutrition decisions, consult a registered dietitian or healthcare provider.<br />
        <span className="protein-muted">
          Inputs are calculated in your browser and are not saved or sent to our servers.<br />
        </span>
      </div>
    </div>
  );
}

export default ProteinIntakeCalculator;